import { Day } from "@/offering/day_utils";
import { Time } from "@/offering/time_utils";

/**
 * A recurring booking as the dashboard reads it: the rule behind a set of
 * bookings sold as one order.
 *
 * ── Vocabulary ───────────────────────────────────────────────────────────────
 * Three layers, three names, and only the middle one is code-only:
 *
 *   Order          the money            — one order, one payment, one receipt
 *   BookingSeries  the recurrence       — this file's subject
 *   Booking        one date             — N of them, each an ordinary booking
 *
 * `BookingSeries`, `booking_series_id` and `occurrence_index` are the schema's
 * names and match OfficeRnD's and Microsoft Graph's API vocabulary, so the
 * integration work stays a field-for-field mapping. They stay in identifiers.
 *
 * They never reach the screen. In partner- and customer-facing copy the whole
 * thing is **a recurring booking**, one date is **a booking** or **a date**, and
 * the control is **Repeat**. "Series", "occurrence", "instance" and "session"
 * are all out — see `recurring_booking_copy.ts`, which owns every string.
 *
 * ── Backend contract ─────────────────────────────────────────────────────────
 * Served by `Orders::BuildRecurringBookingSummary` on the v2 order payload the
 * detail page reads, and by the v3 listing's line-item serializer for the two
 * stamps. Every field stays optional at the marshaller and every reader treats
 * absent as "not a recurring booking", which is what every non-recurring order
 * sends.
 *
 *   order.recurring_booking = {              // the rule, once per order
 *     id, frequency, interval, weekday, anchor_date,
 *     start_time, end_time, duration_unit,
 *     occurrence_count, skipped_dates, skipped_date_reasons,
 *     refundable_until
 *   }
 *   order_item.occurrence_index              // 1-based, on each booking line
 *   order_item.booking_series_id
 *
 * The per-date rows are *derived* from the booking `orderItems` the payload
 * already carries — date, time, status, price are all on them — so the aggregate
 * the backend owes is the rule and the two stamps, not a parallel list that
 * could disagree with the lines.
 */

/** The only frequency phase 1 ships. Kept as a union so a later preset is additive. */
export type RecurrenceFrequency = "weekly";

/** 1–4. Every preset is `anchor + 7 * k * interval`; see `BookingSeries`. */
export type RecurrenceInterval = 1 | 2 | 3 | 4;

/** `Booking#duration_unit`. A daily recurring booking is one whole day per date. */
export type RecurrenceDurationUnit = "hour" | "day";

/**
 * Why a date on the pattern was never booked, as the availability endpoint
 * reports it. Mirrors `Bookings::Logic::UnavailabilityReason::REASONS`; a value
 * outside this list is dropped at the marshaller so the footnote falls back to
 * the bare date rather than rendering an unknown label.
 */
export const SKIPPED_DATE_REASONS = [
  "booked",
  "blockout",
  "closed",
  "lead_time",
  "cutoff",
  "min_duration",
  "elapsed",
] as const;

export type SkippedDateReason = typeof SKIPPED_DATE_REASONS[number];

export interface SkippedDate {
  date: Day;
  /**
   * Absent whenever the backend has only the date.
   *
   * `booking_series.skipped_dates` stays a bare `date[]` (it is the EXDATE set
   * for the calendar); the reason the picker reported at quote is persisted
   * beside it as `skipped_date_reasons`, keyed by date. A date the storefront
   * sent no reason for has none here — the footnote never guesses one.
   */
  reason?: SkippedDateReason;
}

export interface RecurringBooking {
  /** `booking_series.id`. */
  id: string;
  frequency: RecurrenceFrequency;
  interval: RecurrenceInterval;
  /** 0–6, Sunday = 0 — the `block_out_dates` convention. */
  weekday: number;
  /** The first date, i.e. `occurrence_index` 1. */
  anchorDate: Day;
  /** `HHmm`. Both null on a daily recurring booking. */
  startTime?: Time;
  endTime?: Time;
  durationUnit: RecurrenceDurationUnit;
  /** How many dates the customer asked for, before any skip. */
  occurrenceCount: number;
  /** Dates on the pattern that were unavailable when it was booked. */
  skippedDates: SkippedDate[];
  /**
   * The last moment the whole recurring booking can be cancelled with a full
   * refund — the first date's 100%-tier cutoff, not `cancellation_allowed_before`
   * (which is the last tier that pays anything at all). ISO 8601.
   *
   * Absent where no full refund is ever available: a room with no cancellation
   * tiers resolves to non-cancellable from the moment of purchase.
   */
  refundableUntil?: string;
}

function toSkippedDateReason(value: unknown): SkippedDateReason | undefined {
  return (SKIPPED_DATE_REASONS as readonly unknown[]).includes(value)
    ? (value as SkippedDateReason)
    : undefined;
}

/**
 * Reads the bare `date[]` the series stores, annotating each date with the
 * reason from the sibling `skipped_date_reasons` map when one was recorded and
 * is in the vocabulary. Anything that is not a date string is dropped rather
 * than rendered as a date-shaped blank.
 */
export function toSkippedDates(
  data: unknown,
  reasons?: unknown
): SkippedDate[] {
  if (!Array.isArray(data)) return [];

  const reasonMap =
    reasons && typeof reasons === "object"
      ? (reasons as Record<string, unknown>)
      : undefined;

  return data.flatMap((entry): SkippedDate[] =>
    typeof entry === "string"
      ? [{ date: entry, reason: toSkippedDateReason(reasonMap?.[entry]) }]
      : []
  );
}

/**
 * Absent, null, or a payload with no id all mean "not a recurring booking" —
 * which is every order today. Returning undefined rather than a half-built rule
 * keeps the presence check at the call sites a single truthiness test.
 */
export function toRecurringBooking(data: any): RecurringBooking | undefined {
  if (!data?.id) return undefined;

  return {
    id: data.id,
    frequency: data.frequency,
    interval: Number(data.interval) as RecurrenceInterval,
    weekday: Number(data.weekday),
    anchorDate: data.anchor_date,
    startTime: data.start_time ?? undefined,
    endTime: data.end_time ?? undefined,
    durationUnit: data.duration_unit,
    occurrenceCount: Number(data.occurrence_count),
    skippedDates: toSkippedDates(data.skipped_dates, data.skipped_date_reasons),
    // The backend also serves a `refundable` boolean judged on its clock at
    // fetch time; the dashboard judges the same deadline itself on each render
    // (recurring_booking_state.ts). Neither is the enforcement: the cancel
    // endpoint refuses a series past this deadline, so a stale tab's click is
    // refused rather than refunded.
    refundableUntil: data.refundable_until ?? undefined,
  };
}
